import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import instance from '../../../../axios-default';
import { easySnackbar } from '../../../../store/Noti/noti.actions';
import moment from 'moment';
import { Box, InputAdornment, TextField, Typography, makeStyles } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import EditIcon from '@material-ui/icons/Edit';
import style from '../../clinicalSimulation.module.css';
import { handleTriggerRefetch } from '../../RequestComment/RequestCommentSlice';
import useCountdownTimer from '../../../../hooks/useCountDownTimer';
import { minutesLimit } from './RequestMessage';
import ChangeRequestReminderModal from '../ChangeRequestReminderModal/ChangeRequestReminderModal';
import { COMMENT_TYPE, resetDraftComment, setDraftComment, } from '../../../../store/ClinicalSimulation/clinicalSimulation';
import useRequestCommentContext from '../../hooks/useRequestCommentContext';
import EditInfo, { EDIT_INFO_TYPE } from '../EditInfo/EditInfo';
import { useLang } from '../../../../util/lang';

const useStyles = makeStyles(() => ({
    noBorder: {
        '& fieldset': { border: 'none !important', },
    },
    readOnlyPadding: {
        padding: '0px !important',
        '& .MuiOutlinedInput-inputMultiline': { padding: '0px !important', },
    },
    editableNoPadding: {
        padding: '0px !important',
        '& .MuiOutlinedInput-inputMultiline': { padding: '8px !important', },
    },
    commentInputLabel: {
        fontSize: '12px !important',
        fontWeight: '600',
        '& *': { fontSize: '10px !important', fontWeight: '600', fontFamily: 'Montserrat !important', }
    }
}));

const InputOptions = ({ handleSubmit, defaultValue, isEdit, setIsEdit, noTimeLimit = true }) => {
    if (noTimeLimit || defaultValue === '') {
        return (
            <InputAdornment style={{ position: 'absolute', bottom: 16, right: 8, }} position="end">
                {!isEdit ? (
                    <Box
                        onClick={() => {
                            setIsEdit(true);
                        }}
                    >
                        <EditIcon style={{ cursor: 'pointer', fontSize: '18px' }} className={style.messageButton} />
                    </Box>
                ) : (
                    <Box
                        onClick={() => {
                            handleSubmit();
                        }}
                    >
                        <SendIcon style={{ cursor: 'pointer', fontSize: '18px' }} className={style.messageButton} />
                    </Box>
                )}
            </InputAdornment>
        );
    } else {
        return <></>;
    }
};

const RequestInput = ({
    label,
    defaultValue,
    readOnly = false,
    caseId,
    planId,
    updatedDate,
    createdDate,
    noTimeLimit,
    byPassShowingSubmitButton = false,
    showChangeRequestReminder = false,
    planIdForDraftMsg,
}) => {
    const get = useLang();
    const dispatch = useDispatch();
    const classes = useStyles();

    const [initialDefaultValue, setInitialDefaultValue] = useState('');

    const [value, setValue] = useState(defaultValue);
    const [errorMessage, setErrorMessage] = useState('');
    const [isShowSubmitButton, setIsShowSubmitButton] = useState(true);
    const [isEdit, setIsEdit] = useState(false);
    const [tabFocus, setTabFocus] = useState(true);
    const [open, setOpen] = useState(false);

    const { time, resetTimer, seconds } = useCountdownTimer(0);

    const { setLoading } = useRequestCommentContext();

    const readOnlyFlag = readOnly || !isShowSubmitButton || !isEdit || !noTimeLimit;
    const editableFlag = !readOnly && isShowSubmitButton && noTimeLimit;

    const handleOnChange = useCallback(
        (e) => {
            setValue(e.target.value);
            dispatch(
                setDraftComment({
                    currentPlanId: planIdForDraftMsg || 'NO_PLAN_ID',
                    value: e.target.value,
                    type: COMMENT_TYPE.DOCTOR_REQUEST,
                })
            );
        },
        [planIdForDraftMsg]
    );

    const handleSubmit = useCallback(async () => {
        setIsEdit(false);
        console.log('caseId: ', caseId);

        if (/^\s*$/.test(value)) {
            setErrorMessage('Input should not be empty or contain only spaces');
        } else {
            setErrorMessage('');

            if (showChangeRequestReminder) {
                setOpen(true);
            } else {
                await submitData();
            }
        }
    }, [value, createdDate, showChangeRequestReminder]);

    const submitData = async () => {
        const data = {
            value: value,
            plan_id: planId,
        };

        try {
            setLoading(true);
            const result = await instance.post(`case/treatment_plan_request/${caseId}`, data);
            setIsShowSubmitButton(!result.data.success || noTimeLimit);
            if (defaultValue === '') {
                setInitialDefaultValue(result.data.results);
            }
            if (result.data.success) {
                dispatch(resetDraftComment());
                setOpen(false);
                dispatch(easySnackbar('Updated Doctor Request', 'success'));
                dispatch(handleTriggerRefetch());
            }
        } catch (e) {
            if (e.response && e.response.data && e.response.data.message) {
                dispatch(easySnackbar(e.response.data.message, 'error'));
            } else {
                dispatch(easySnackbar('An error occurred, please try again', 'error'));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setTabFocus(true);
            } else {
                setTabFocus(false);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        if (tabFocus) {
            if (!planId) {
                const inputDate = moment(createdDate).utc();
                const currentDate = moment().utc();
                const diffSecond = currentDate.diff(inputDate, 'seconds');
                resetTimer(diffSecond <= minutesLimit * 60 ? minutesLimit * 60 - diffSecond : 0);
            }
        }
    }, [createdDate, tabFocus, planId]);

    useEffect(() => {
        if (!byPassShowingSubmitButton) {
            if (seconds === 0 && createdDate) {
                setIsShowSubmitButton(false);
            } else if (planId) {
                setIsShowSubmitButton(false);
            } else {
                setIsShowSubmitButton(true);
            }
        } else {
            setIsShowSubmitButton(true);
        }
    }, [seconds, createdDate, planId]);

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    return (
        <>
            <Box>
                <Box style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", alignItems: "center" }}>
                    <Typography className={classes.commentInputLabel}>
                        {label}
                    </Typography>
                    <EditInfo type={EDIT_INFO_TYPE.REQUEST} updatedDate={updatedDate} created_date={createdDate} time={time} />
                </Box>
                <TextField
                    multiline
                    minRows={4}
                    style={{ width: '100%' }}
                    variant={'outlined'}
                    InputProps={{
                        readOnly: readOnlyFlag,
                        classes: {
                            root: `${!editableFlag ? `${classes.readOnlyPadding} ${classes.noBorder}` : classes.editableNoPadding}`
                        },
                        startAdornment: editableFlag && (
                            <InputOptions
                                handleSubmit={handleSubmit}
                                defaultValue={initialDefaultValue !== '' ? initialDefaultValue : defaultValue}
                                isEdit={isEdit}
                                setIsEdit={setIsEdit}
                                noTimeLimit={noTimeLimit}
                            />
                        )
                    }}
                    inputProps={{ style: { fontSize: '14px', } }}
                    InputLabelProps={{ shrink: true, }}
                    value={value ?? ''}
                    onChange={handleOnChange}
                />
            </Box>
            <Box color="red" marginY={0.5} fontSize={10.5} fontWeight="600" textAlign="justify">
                {errorMessage}
            </Box>
            <Box color="red" marginY={0.5} fontSize={10.5} fontWeight="600" textAlign="justify">
                {get('@TreatmentPlanHistory.importantNote')}
            </Box>
            {showChangeRequestReminder && (
                <>
                    <ChangeRequestReminderModal setOpen={setOpen} open={open} handleDataSave={submitData} />
                </>
            )}
        </>
    );
};

export default memo(RequestInput);